/**
 * Company Profile Page
 * 
 * Allows users to view and edit their company/business information
 * that was initially set during onboarding.
 * 
 * Users can update:
 * - Company details (name, industry, size)
 * - Supply chain configuration
 * - Key products and categories
 * - Supplier information
 * - Warehouse/location data
 * - Integration settings
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Chip,
  IconButton,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Business,
  Save,
  Edit,
  Add,
  Delete,
  Refresh,
  Category,
  LocalShipping,
  Warehouse,
  People,
  Settings,
  Info,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { getCompany, updateCompany } from '../../api/companyService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface CompanyData {
  id: string;
  name: string;
  industry: string;
  company_size: string;
  country: string;
  currency: string;
  timezone: string;
  
  // Supply chain specifics
  supply_chain_type: string;
  primary_products: string[];
  product_categories: string[];
  
  // Suppliers
  key_suppliers: Array<{
    name: string;
    category: string;
    country: string;
    criticality: string;
  }>;
  
  // Locations
  warehouses: Array<{
    name: string;
    location: string;
    type: string;
  }>;
  
  // Preferences
  risk_tolerance: string;
  sustainability_priority: string;
  
  onboarding_completed: boolean;
}

const industries = [
  'Manufacturing',
  'Retail',
  'Automotive',
  'Pharmaceutical',
  'Electronics',
  'Food & Beverage',
  'Aerospace',
  'Chemicals',
  'Textiles',
  'Consumer Goods',
  'Other',
];

const companySizes = [
  'Startup (1-10)',
  'Small (11-50)',
  'Medium (51-200)',
  'Large (201-1000)',
  'Enterprise (1000+)',
];

const supplyChainTypes = [
  'Make to Stock',
  'Make to Order',
  'Assemble to Order',
  'Engineer to Order',
  'Hybrid',
];

const CompanyProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(false);
  
  const [companyData, setCompanyData] = useState<CompanyData>({
    id: '',
    name: '',
    industry: '',
    company_size: '',
    country: 'United Kingdom',
    currency: 'GBP',
    timezone: 'Europe/London',
    supply_chain_type: '',
    primary_products: [],
    product_categories: [],
    key_suppliers: [],
    warehouses: [],
    risk_tolerance: 'Medium',
    sustainability_priority: 'Medium',
    onboarding_completed: false,
  });

  // Dialog states
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', category: '', country: '', criticality: 'Medium' });
  const [newWarehouse, setNewWarehouse] = useState({ name: '', location: '', type: 'Distribution' });
  const [newProduct, setNewProduct] = useState('');
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    loadCompanyData();
  }, [user]);

  const loadCompanyData = async () => {
    if (!user?.company_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const company = await getCompany(user.company_id);
      if (company) {
        setCompanyData({
          ...companyData,
          ...company,
          primary_products: company.primary_products || [],
          product_categories: company.product_categories || [],
          key_suppliers: company.key_suppliers || [],
          warehouses: company.warehouses || [],
        });
      }
    } catch (err) {
      console.error('Failed to load company data:', err);
      setError('Failed to load company information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      await updateCompany(companyData.id, companyData);
      
      setSuccess('Company profile updated successfully!');
      setEditMode(false);
    } catch (err) {
      console.error('Failed to save company data:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddProduct = () => {
    if (newProduct.trim() && !companyData.primary_products.includes(newProduct.trim())) {
      setCompanyData({
        ...companyData,
        primary_products: [...companyData.primary_products, newProduct.trim()],
      });
      setNewProduct('');
    }
  };

  const handleRemoveProduct = (product: string) => {
    setCompanyData({
      ...companyData,
      primary_products: companyData.primary_products.filter(p => p !== product),
    });
  };

  const handleAddCategory = () => {
    if (newCategory.trim() && !companyData.product_categories.includes(newCategory.trim())) {
      setCompanyData({
        ...companyData,
        product_categories: [...companyData.product_categories, newCategory.trim()],
      });
      setNewCategory('');
    }
  };

  const handleRemoveCategory = (category: string) => {
    setCompanyData({
      ...companyData,
      product_categories: companyData.product_categories.filter(c => c !== category),
    });
  };

  const handleAddSupplier = () => {
    if (newSupplier.name.trim()) {
      setCompanyData({
        ...companyData,
        key_suppliers: [...companyData.key_suppliers, { ...newSupplier }],
      });
      setNewSupplier({ name: '', category: '', country: '', criticality: 'Medium' });
      setSupplierDialogOpen(false);
    }
  };

  const handleRemoveSupplier = (index: number) => {
    setCompanyData({
      ...companyData,
      key_suppliers: companyData.key_suppliers.filter((_, i) => i !== index),
    });
  };

  const handleAddWarehouse = () => {
    if (newWarehouse.name.trim()) {
      setCompanyData({
        ...companyData,
        warehouses: [...companyData.warehouses, { ...newWarehouse }],
      });
      setNewWarehouse({ name: '', location: '', type: 'Distribution' });
      setWarehouseDialogOpen(false);
    }
  };

  const handleRemoveWarehouse = (index: number) => {
    setCompanyData({
      ...companyData,
      warehouses: companyData.warehouses.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Business sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Company Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your business information and supply chain configuration
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {editMode ? (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setEditMode(false);
                  loadCompanyData(); // Reset changes
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSave}
                disabled={saving}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadCompanyData}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => setEditMode(true)}
              >
                Edit Profile
              </Button>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }} icon={<Info />}>
        This information helps ASCIN provide better supply chain insights, risk assessments, 
        and AI-powered recommendations tailored to your business.
      </Alert>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<Business />} label="Company Details" iconPosition="start" />
          <Tab icon={<Category />} label="Products" iconPosition="start" />
          <Tab icon={<People />} label="Suppliers" iconPosition="start" />
          <Tab icon={<Warehouse />} label="Locations" iconPosition="start" />
          <Tab icon={<Settings />} label="Preferences" iconPosition="start" />
        </Tabs>

        {/* Company Details Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={companyData.name}
                  onChange={(e) => setCompanyData({ ...companyData, name: e.target.value })}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Industry</InputLabel>
                  <Select
                    value={companyData.industry}
                    label="Industry"
                    onChange={(e) => setCompanyData({ ...companyData, industry: e.target.value })}
                  >
                    {industries.map((ind) => (
                      <MenuItem key={ind} value={ind}>{ind}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Company Size</InputLabel>
                  <Select
                    value={companyData.company_size}
                    label="Company Size"
                    onChange={(e) => setCompanyData({ ...companyData, company_size: e.target.value })}
                  >
                    {companySizes.map((size) => (
                      <MenuItem key={size} value={size}>{size}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Supply Chain Type</InputLabel>
                  <Select
                    value={companyData.supply_chain_type}
                    label="Supply Chain Type"
                    onChange={(e) => setCompanyData({ ...companyData, supply_chain_type: e.target.value })}
                  >
                    {supplyChainTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Country"
                  value={companyData.country}
                  onChange={(e) => setCompanyData({ ...companyData, country: e.target.value })}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Currency"
                  value={companyData.currency}
                  onChange={(e) => setCompanyData({ ...companyData, currency: e.target.value })}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Timezone"
                  value={companyData.timezone}
                  onChange={(e) => setCompanyData({ ...companyData, timezone: e.target.value })}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Products Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 3 }}>
            <Typography variant="h6" gutterBottom>Primary Products</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              List your main products that the AI will track and optimize
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {companyData.primary_products.map((product) => (
                <Chip
                  key={product}
                  label={product}
                  onDelete={editMode ? () => handleRemoveProduct(product) : undefined}
                  color="primary"
                  variant="outlined"
                />
              ))}
              {companyData.primary_products.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No products added yet
                </Typography>
              )}
            </Box>

            {editMode && (
              <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
                <TextField
                  size="small"
                  placeholder="Add a product..."
                  value={newProduct}
                  onChange={(e) => setNewProduct(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddProduct()}
                />
                <Button variant="outlined" startIcon={<Add />} onClick={handleAddProduct}>
                  Add
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Product Categories</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Categories help organize and classify your products
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {companyData.product_categories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  onDelete={editMode ? () => handleRemoveCategory(category) : undefined}
                  color="secondary"
                  variant="outlined"
                />
              ))}
              {companyData.product_categories.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No categories added yet
                </Typography>
              )}
            </Box>

            {editMode && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Add a category..."
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                />
                <Button variant="outlined" startIcon={<Add />} onClick={handleAddCategory}>
                  Add
                </Button>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Suppliers Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ px: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">Key Suppliers</Typography>
                <Typography variant="body2" color="text.secondary">
                  Suppliers are monitored for risk and disruption alerts
                </Typography>
              </Box>
              {editMode && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setSupplierDialogOpen(true)}
                >
                  Add Supplier
                </Button>
              )}
            </Box>

            <List>
              {companyData.key_suppliers.map((supplier, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={supplier.name}
                    secondary={`${supplier.category} • ${supplier.country} • Criticality: ${supplier.criticality}`}
                  />
                  {editMode && (
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleRemoveSupplier(index)}>
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
              {companyData.key_suppliers.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No suppliers added"
                    secondary="Add your key suppliers to enable supply chain monitoring"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </TabPanel>

        {/* Locations Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ px: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">Warehouses & Locations</Typography>
                <Typography variant="body2" color="text.secondary">
                  Your distribution centers and storage facilities
                </Typography>
              </Box>
              {editMode && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setWarehouseDialogOpen(true)}
                >
                  Add Location
                </Button>
              )}
            </Box>

            <List>
              {companyData.warehouses.map((warehouse, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={warehouse.name}
                    secondary={`${warehouse.location} • ${warehouse.type}`}
                  />
                  {editMode && (
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleRemoveWarehouse(index)}>
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
              {companyData.warehouses.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No locations added"
                    secondary="Add your warehouses and distribution centers"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </TabPanel>

        {/* Preferences Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ px: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Risk Tolerance</InputLabel>
                  <Select
                    value={companyData.risk_tolerance}
                    label="Risk Tolerance"
                    onChange={(e) => setCompanyData({ ...companyData, risk_tolerance: e.target.value })}
                  >
                    <MenuItem value="Low">Low - Prefer safer, more conservative strategies</MenuItem>
                    <MenuItem value="Medium">Medium - Balanced approach</MenuItem>
                    <MenuItem value="High">High - Accept more risk for better returns</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Affects AI recommendations for supplier selection and inventory levels
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Sustainability Priority</InputLabel>
                  <Select
                    value={companyData.sustainability_priority}
                    label="Sustainability Priority"
                    onChange={(e) => setCompanyData({ ...companyData, sustainability_priority: e.target.value })}
                  >
                    <MenuItem value="Low">Low - Cost is primary driver</MenuItem>
                    <MenuItem value="Medium">Medium - Balance cost and sustainability</MenuItem>
                    <MenuItem value="High">High - Prioritize eco-friendly options</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Influences supplier scoring and route optimization
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Paper>

      {/* Add Supplier Dialog */}
      <Dialog open={supplierDialogOpen} onClose={() => setSupplierDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Supplier</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Supplier Name"
                value={newSupplier.name}
                onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Category"
                value={newSupplier.category}
                onChange={(e) => setNewSupplier({ ...newSupplier, category: e.target.value })}
                placeholder="e.g., Raw Materials, Components"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                value={newSupplier.country}
                onChange={(e) => setNewSupplier({ ...newSupplier, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Criticality</InputLabel>
                <Select
                  value={newSupplier.criticality}
                  label="Criticality"
                  onChange={(e) => setNewSupplier({ ...newSupplier, criticality: e.target.value })}
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupplierDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSupplier}>Add Supplier</Button>
        </DialogActions>
      </Dialog>

      {/* Add Warehouse Dialog */}
      <Dialog open={warehouseDialogOpen} onClose={() => setWarehouseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Location</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location Name"
                value={newWarehouse.name}
                onChange={(e) => setNewWarehouse({ ...newWarehouse, name: e.target.value })}
                placeholder="e.g., London Distribution Centre"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address/Location"
                value={newWarehouse.location}
                onChange={(e) => setNewWarehouse({ ...newWarehouse, location: e.target.value })}
                placeholder="e.g., London, UK"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newWarehouse.type}
                  label="Type"
                  onChange={(e) => setNewWarehouse({ ...newWarehouse, type: e.target.value })}
                >
                  <MenuItem value="Distribution">Distribution Centre</MenuItem>
                  <MenuItem value="Warehouse">Warehouse</MenuItem>
                  <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                  <MenuItem value="Fulfilment">Fulfilment Centre</MenuItem>
                  <MenuItem value="Office">Office</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarehouseDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddWarehouse}>Add Location</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        message={success}
      />
    </Box>
  );
};

export default CompanyProfilePage;
